import Api from "../../../../../../../../core/services/api/api";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toImportCodeUpper, toPartNumberUpper} from "./shared";

/**
 * Warehouse alert thresholds — phased validation (see {@link BaseImportTransformer} run contract).
 * Uses `partNo` + `warehouseCode` from mapped rows; optional `warehouses` avoids a fetch when provided.
 *
 * @param {Array<Object|null|undefined>} mappedData
 * @param {boolean} isFinalRun
 * @param {Array<*>} [warehouses] When null/undefined, warehouses are loaded from the API.
 */
export class WarehouseAlertThresholdsImportTransformer extends BaseImportTransformer {
    async run(mappedData, isFinalRun = false, warehouses = null) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, warehouseCodeUpper: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNo),
            warehouseCodeUpper: toImportCodeUpper(item?.warehouseCode),
            codes: [],
        }));

        // ── Phase 1: preliminary (CSV-only) — same “seed row” as legacy: both part + warehouse required ──
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.partNoUpper || !rs.warehouseCodeUpper) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        // ── Phase 2: warehouse list + map ─────────────────────────────────────────
        let warehouseList = warehouses;
        if (!warehouseList) {
            const warehousesResponse = await Api.getOperatorWarehouses();
            warehouseList = warehousesResponse?.data ?? [];
        }
        const warehouseMap = Object.fromEntries(warehouseList.map((w) => [w.code.toUpperCase(), w.id]));

        // ── Phase 3: batched part lookup (legacy only queried parts for seed-valid rows) ──
        const uniqueParts = [
            ...new Set(
                rowState
                    .filter((r) => r.item && r.partNoUpper && r.warehouseCodeUpper)
                    .map((r) => r.partNoUpper)
            ),
        ].sort();
        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        // ── Phase 4: unknown part (only rows that passed the seed filter, like legacy getPartIds input) ──
        for (const rs of rowState) {
            if (!rs.item || !rs.partNoUpper || !rs.warehouseCodeUpper) continue;
            if (!partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Phase 5: unknown warehouse code (seed-valid rows only, matching legacy) ──
        for (const rs of rowState) {
            if (!rs.item || !rs.partNoUpper || !rs.warehouseCodeUpper) continue;
            if (!warehouseMap[rs.warehouseCodeUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_WAREHOUSE);
            }
        }

        // ── Phase 6: numeric thresholds (legacy: only evaluated on seed-valid rows) ──
        for (const rs of rowState) {
            if (!rs.item || !rs.partNoUpper || !rs.warehouseCodeUpper) continue;
            const {item} = rs;
            const reOrderPoint =
                item.reOrderPoint !== undefined && item.reOrderPoint !== null && item.reOrderPoint !== ""
                    ? parseFloat(item.reOrderPoint.toString())
                    : NaN;
            const minimumOrder =
                item.minimumOrder !== undefined && item.minimumOrder !== null && item.minimumOrder !== ""
                    ? parseFloat(item.minimumOrder.toString())
                    : NaN;
            if (Number.isNaN(reOrderPoint) || Number.isNaN(minimumOrder)) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        // ── Emit merged issues ─────────────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.MISSING_REQUIRED_FIELDS],
                });
                continue;
            }
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun
                    ? null
                    : {warehouseId: null, partId: null, reOrderPoint: "", minimumOrder: ""};
            }

            const rs = rowState[index];
            const partId = partIds[rs.partNoUpper];
            const warehouseId = warehouseMap[rs.warehouseCodeUpper];

            const reOrderPoint =
                item.reOrderPoint !== undefined && item.reOrderPoint !== null && item.reOrderPoint !== ""
                    ? parseFloat(item.reOrderPoint.toString())
                    : NaN;
            const minimumOrder =
                item.minimumOrder !== undefined && item.minimumOrder !== null && item.minimumOrder !== ""
                    ? parseFloat(item.minimumOrder.toString())
                    : NaN;

            if (issueIndexSet.has(index)) {
                return isFinalRun
                    ? null
                    : {
                        warehouseId: warehouseId ?? null,
                        partId: partId ?? null,
                        reOrderPoint: Number.isNaN(reOrderPoint) ? (item.reOrderPoint ?? "") : reOrderPoint,
                        minimumOrder: Number.isNaN(minimumOrder) ? (item.minimumOrder ?? "") : minimumOrder,
                    };
            }

            return {
                warehouseId,
                partId,
                reOrderPoint,
                minimumOrder,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
